import { call, put } from 'redux-saga/effects';
import { UserService } from '../../../services/User';
import { loadCustomers } from '../customers/actions';
import { cleanDashboard } from '../dashboard/actions';
import { clearInvoicesState } from '../invoices/actions';
import { clearOrdersState } from '../long-steel-orders/actions';
import { EOrderTab } from '../long-steel-orders/types';
import { cleanShoppingCart } from '../long-steel-shopping-cart/actions';
import {
  loadUserSuccess,
  loadUserFailure,
  saveUserSuccess,
  saveUserFailure,
  tokenValidateSuccess,
  tokenValidateFailure,
  loadUser,
  tokenValidate
} from './actions';
import {
  ECustomerTypeView,
  EResearchNps,
  IUser,
  IUserPreference,
  Types
} from './types';
import { clearFabricationContracts } from '../fabrication-contracts/actions';
import { clearContractsAndConstructionsState } from '../contracts-and-constructions/actions';
import { clearFabricationOrdersState } from '../fabrication-orders/actions';
import { cleanDownloadAreaState } from '../download-area/action';
import { addToast } from '../toast/actions';
import { EToastTypes } from '../toast/types';
import { RefreshAuthTokenService } from '../../../services/Auth';
import { CookiesKey } from '../../../services/Auth/enums';
import { deleteCookie } from '../../../services/MVP/utils';

interface ILoadUserParams {
  payload: {
    userId: string;
  };
  type: Types;
}
interface ISaveUserParams {
  payload: {
    user: IUser;
    userPreference?: IUserPreference;
    sendOrdersToEmail?: boolean;
    sendFinancialToEmail?: boolean;
    userAcceptNotification?: boolean;
    improvementResearch?: boolean;
    privacyTerms?: boolean;
    userName?: string;
    improvementResearchNps: EResearchNps;
    jorneySurveyShowcaseStatus: EResearchNps;
    jorneySurveyHistoryStatus: EResearchNps;
    jorneySurveyItemsStatus: EResearchNps;
    jorneySurveySheetStatus: EResearchNps;
    gerdauProModal?: boolean;
  };
  type: Types;
}

interface ICallLogout {
  payload: {
    toCallLogout: boolean;
  };
  type: Types;
}

export function* tokenValidateSaga() {
  const userService = new UserService();
  try {
    const user: IUser = yield call(userService.validate.bind(userService));
    yield put(tokenValidateSuccess(user.id, user.expecialSteel));

    yield put(loadUser(user.id));
  } catch (err) {
    console.error('tokenValidateSaga', err);
    yield put(tokenValidateFailure(err));
    RefreshAuthTokenService.checkAndDeleteCookie(
      CookiesKey.AUTHORIZATION_TOKEN
    );
    RefreshAuthTokenService.checkAndDeleteCookie(
      CookiesKey.AUTHORIZATION_TOKEN_EXPIRATION
    );
    userService.logout();
  }
}

export function* loadUserSaga(params: ILoadUserParams) {
  const userService = new UserService();
  try {
    const { userId } = params.payload;
    const user: IUser = yield call(
      userService.getUser.bind(userService),
      userId
    );
    user.searchCustomerType = ECustomerTypeView.EMITTER;
    yield put(loadUserSuccess(user));
    yield put(loadCustomers(user));
  } catch (err) {
    console.error('loadUserSaga', err);
    yield put(loadUserFailure(err));
    userService.logout();
  }
}

export function* saveUserSaga(params: ISaveUserParams) {
  try {
    const userService = new UserService();
    const {
      user,
      userPreference,
      sendOrdersToEmail,
      sendFinancialToEmail,
      userAcceptNotification,
      improvementResearch,
      privacyTerms,
      userName,
      improvementResearchNps,
      jorneySurveyShowcaseStatus,
      jorneySurveyHistoryStatus,
      jorneySurveyItemsStatus,
      jorneySurveySheetStatus,
      gerdauProModal
    } = params.payload;

    const newUser: IUser = yield call(
      userService.saveUser.bind(userService),
      user,
      userPreference,
      sendOrdersToEmail,
      sendFinancialToEmail,
      userAcceptNotification,
      undefined,
      improvementResearch,
      privacyTerms,
      userName,
      improvementResearchNps,
      jorneySurveyShowcaseStatus,
      jorneySurveyHistoryStatus,
      jorneySurveyItemsStatus,
      jorneySurveySheetStatus,
      gerdauProModal
    );
    const hasSurvey =
      [
        improvementResearchNps,
        jorneySurveyShowcaseStatus,
        jorneySurveyHistoryStatus,
        jorneySurveyItemsStatus,
        jorneySurveySheetStatus
      ].filter((el) => el).length > 0;
    if (
      (userPreference && userPreference?.name === 'order-entry') ||
      hasSurvey
    ) {
      return;
    }
    yield put(saveUserSuccess(newUser));
  } catch (err) {
    console.error('saveUserSaga', err);
    yield put(saveUserFailure(err));
    yield put(
      addToast({
        message: 'Falha ao salvar o usuário.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
  }
}

export function* swapCustomerViewTypeSaga(params: ISaveUserParams) {
  try {
    const { user } = params.payload;
    const userService = new UserService();

    const receiverCustomerViewType =
      user.roles.fabricationViewCustomers &&
      user.roles.fabricationViewCustomers.length > 0
        ? ECustomerTypeView.FABRICATION_RECEIVER
        : ECustomerTypeView.RECEIVER;

    const swapToCustomerViewType =
      user.searchCustomerType === ECustomerTypeView.EMITTER
        ? receiverCustomerViewType
        : ECustomerTypeView.EMITTER;

    // limpa localstorage e estados
    userService.cleanLocalStorage();
    yield put(clearOrdersState(EOrderTab.OPEN));
    yield put(clearInvoicesState());
    yield put(clearFabricationContracts());
    yield put(clearContractsAndConstructionsState());
    yield put(clearFabricationOrdersState());
    yield put(cleanDownloadAreaState());

    const updatedUser = { ...user, searchCustomerType: swapToCustomerViewType };
    yield put(loadUserSuccess(updatedUser));
    yield put(loadCustomers(updatedUser));
  } catch (err) {
    console.error('swapCustomerViewTypeSaga', err);
    yield put(loadUserFailure(err));
  }
}

export function* userLogoutSaga(data: ICallLogout) {
  const { toCallLogout } = data.payload;
  try {
    if (toCallLogout) {
      const userService = new UserService();
      yield userService.userLogout();
    }
    // limpa estados
    yield put(clearOrdersState(EOrderTab.OPEN));
    yield put(clearInvoicesState());
    yield put(cleanDashboard());
    yield put(cleanShoppingCart());
  } catch (err) {
    console.error('userLogoutSaga', err);
  }
}

export function* resetUserSessionSaga(data?: {
  payload?: { keepSession?: boolean };
}) {
  const { keepSession } = data?.payload ?? {};

  // mesmo usuário reautenticando: apenas renova a sessão sem resetar a jornada (carrinho/cliente)
  if (keepSession) {
    return;
  }

  try {
    const userService = new UserService();
    // limpa dados vinculados ao usuário anterior antes de carregar o novo usuário autenticado
    yield put(clearOrdersState(EOrderTab.OPEN));
    yield put(clearInvoicesState());
    yield put(cleanDashboard());
    yield put(cleanShoppingCart());
    yield put(clearFabricationContracts());
    yield put(clearContractsAndConstructionsState());
    yield put(clearFabricationOrdersState());
    yield put(cleanDownloadAreaState());

    // remove dados persistidos (cliente selecionado, carrinho, caches) do usuário anterior
    userService.cleanLocalStorage();
    deleteCookie(CookiesKey.CART_ID);
    deleteCookie(CookiesKey.EMITTER_ID);
    deleteCookie(CookiesKey.RECEIVER_ID);
    deleteCookie(CookiesKey.ORDER_CONTINUOS);

    yield put(tokenValidate());
  } catch (err) {
    console.error('resetUserSessionSaga', err);
  }
}
